import { useEffect } from 'react'
import { WizardFormData } from '../IntegrationWizard'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Info, Search, FileText, Globe, ClipboardList } from 'lucide-react'
import { useSROConfigs } from '@/hooks/useSRO'

interface Step3ConfigurationProps {
  formData: WizardFormData
  setFormData: (data: WizardFormData) => void
  errors: Record<string, string>
  isEditing: boolean
}

const COUNTRY_OPTIONS = [
  { value: 'none', label: 'No country boost' },
  { value: 'united states', label: 'United States' },
  { value: 'united kingdom', label: 'United Kingdom' },
  { value: 'canada', label: 'Canada' },
  { value: 'australia', label: 'Australia' },
  { value: 'germany', label: 'Germany' },
  { value: 'france', label: 'France' },
  { value: 'india', label: 'India' },
  { value: 'japan', label: 'Japan' },
  { value: 'brazil', label: 'Brazil' },
  { value: 'mexico', label: 'Mexico' },
  { value: 'spain', label: 'Spain' },
  { value: 'italy', label: 'Italy' },
  { value: 'netherlands', label: 'Netherlands' },
  { value: 'singapore', label: 'Singapore' },
  { value: 'south korea', label: 'South Korea' },
  { value: 'china', label: 'China' },
  { value: 'russia', label: 'Russia' },
  { value: 'south africa', label: 'South Africa' },
  { value: 'united arab emirates', label: 'United Arab Emirates' },
]

export default function Step3Configuration({ formData, setFormData, errors, isEditing }: Step3ConfigurationProps) {
  // Crawler configuration
  if (formData.integration_type === 'crawler') {
    return (
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Web Crawler Configuration</h2>
          <p className="text-sm text-muted-foreground">
            Configure web search settings for real-time information retrieval
          </p>
        </div>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Web Crawler is managed by Chatterpillar. Contact support if web search is not working.
          </AlertDescription>
        </Alert>

        {/* Search Behavior Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-4">
            <Search className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Search Behavior</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="crawler_search_depth">Search Depth</Label>
              <Select
                value={formData.crawler_search_depth}
                onValueChange={(value: 'basic' | 'advanced') => 
                  setFormData({ ...formData, crawler_search_depth: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic (Faster, 1 credit)</SelectItem>
                  <SelectItem value="advanced">Advanced (More thorough, 2 credits)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Advanced search provides more comprehensive results but uses more API credits.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="crawler_topic">Search Topic</Label>
              <Select
                value={formData.crawler_topic}
                onValueChange={(value: 'general' | 'news' | 'finance') => 
                  setFormData({ ...formData, crawler_topic: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="news">News</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                <strong>General:</strong> Broad searches. <strong>News:</strong> Current events. <strong>Finance:</strong> Financial data.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="crawler_time_range">Time Range</Label>
              <Select
                value={formData.crawler_time_range || 'any'}
                onValueChange={(value) => 
                  setFormData({ ...formData, crawler_time_range: value === 'any' ? '' : value as '' | 'day' | 'week' | 'month' | 'year' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any time</SelectItem>
                  <SelectItem value="day">Past day</SelectItem>
                  <SelectItem value="week">Past week</SelectItem>
                  <SelectItem value="month">Past month</SelectItem>
                  <SelectItem value="year">Past year</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Filter results by recency. Use "Past day" for breaking news.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="crawler_max_results">Maximum Results</Label>
              <Input
                id="crawler_max_results"
                type="number"
                min="1"
                max="20"
                value={formData.crawler_max_results}
                onChange={(e) => setFormData({ ...formData, crawler_max_results: parseInt(e.target.value) || 5 })}
              />
              <p className="text-xs text-muted-foreground">
                Number of search results to return (1-20). More results = more context.
              </p>
            </div>
          </div>
        </div>

        {/* Content Options Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Content Options</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="crawler_include_answer">AI-Generated Answer</Label>
              <Select
                value={formData.crawler_include_answer}
                onValueChange={(value: 'off' | 'basic' | 'advanced') => 
                  setFormData({ ...formData, crawler_include_answer: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off - Sources only</SelectItem>
                  <SelectItem value="basic">Basic - Quick summary</SelectItem>
                  <SelectItem value="advanced">Advanced - Detailed answer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                When enabled, an AI-generated answer is created from search results. This appears before source links in responses.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="crawler_include_raw_content" className="text-sm font-medium">
                    Include Raw Content
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Include cleaned HTML content from each result
                  </p>
                </div>
                <Switch
                  id="crawler_include_raw_content"
                  checked={formData.crawler_include_raw_content}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, crawler_include_raw_content: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="crawler_include_images" className="text-sm font-medium">
                    Include Images
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Include image URLs in search results
                  </p>
                </div>
                <Switch
                  id="crawler_include_images"
                  checked={formData.crawler_include_images}
                  onCheckedChange={(checked) => 
                    setFormData({ 
                      ...formData, 
                      crawler_include_images: checked,
                      crawler_include_image_descriptions: checked ? formData.crawler_include_image_descriptions : false
                    })
                  }
                />
              </div>
            </div>

            {formData.crawler_include_images && (
              <div className="flex items-center justify-between p-3 border rounded-lg ml-4 border-l-4 border-l-primary">
                <div className="space-y-0.5">
                  <Label htmlFor="crawler_include_image_descriptions" className="text-sm font-medium">
                    Include Image Descriptions
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Add AI-generated descriptions for each image
                  </p>
                </div>
                <Switch
                  id="crawler_include_image_descriptions"
                  checked={formData.crawler_include_image_descriptions}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, crawler_include_image_descriptions: checked })
                  }
                />
              </div>
            )}
          </div>
        </div>

        {/* Domain Filtering Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Domain Filtering</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="crawler_country">Country Boost</Label>
              <Select
                value={formData.crawler_country || 'none'}
                onValueChange={(value) => setFormData({ ...formData, crawler_country: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((country) => (
                    <SelectItem key={country.value} value={country.value}>
                      {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Prioritize search results from a specific country. Only works with "General" topic.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="crawler_include_domains">Include Domains (Optional)</Label>
              <Input
                id="crawler_include_domains"
                placeholder="example.com, news.example.org"
                value={formData.crawler_include_domains}
                onChange={(e) => setFormData({ ...formData, crawler_include_domains: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of domains to search exclusively. Example: <code>nytimes.com, bbc.com, reuters.com</code>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="crawler_exclude_domains">Exclude Domains (Optional)</Label>
              <Input
                id="crawler_exclude_domains"
                placeholder="ads.example.com, spam.example.org"
                value={formData.crawler_exclude_domains}
                onChange={(e) => setFormData({ ...formData, crawler_exclude_domains: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of domains to exclude. Example: <code>pinterest.com, quora.com</code>
              </p>
            </div>
          </div>
        </div>

        {/* Query Context Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-4">
            <Search className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Query Context (Optional)</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="crawler_query_prefix">Context Prefix</Label>
              <Textarea
                id="crawler_query_prefix"
                rows={3}
                maxLength={300}
                placeholder='Example: "City of Doral Florida official government services and information."'
                value={formData.crawler_query_prefix}
                onChange={(e) => setFormData({ ...formData, crawler_query_prefix: e.target.value })}
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  This text will be added before every search query to provide context.
                </p>
                <span className="text-xs text-muted-foreground">
                  {formData.crawler_query_prefix.length}/300 characters
                </span>
              </div>
            </div>

            {formData.crawler_query_prefix.trim() && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-xs font-medium text-muted-foreground mb-1">Query Preview:</p>
                <code className="text-xs break-all">
                  {formData.crawler_query_prefix.trim()}\n\n &lt;your query here&gt;
                </code>
              </div>
            )}
          </div>
        </div>

        {/* Quick Tips */}
        <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-xs text-blue-800 dark:text-blue-200">
            <strong>Tips for best results:</strong>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Use <strong>Advanced</strong> search depth for complex queries requiring comprehensive information</li>
              <li>Set <strong>News</strong> topic for current events, sports, and politics</li>
              <li>Enable <strong>AI Answer</strong> to get a synthesized response before source links</li>
              <li>Use <strong>Include Domains</strong> to restrict searches to trusted sources</li>
              <li>Add a <strong>Query Context</strong> to help the crawler understand your domain</li>
            </ul>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // SRO configuration
  if (formData.integration_type === 'sro') {
    return <SROConfigSection formData={formData} setFormData={setFormData} errors={errors} />
  }

  // API configuration (OpenAI Compatible or Generic)
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Connection Configuration</h2>
        <p className="text-sm text-muted-foreground">
          Configure API connection settings and session management
        </p>
      </div>

      {/* Connection Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">API Connection</h3>

        <div className="space-y-2">
          <Label htmlFor="endpoint_url">API Endpoint URL *</Label>
          <Input
            id="endpoint_url"
            type="url"
            placeholder="https://api.example.com/v1/chat/completions"
            value={formData.endpoint_url}
            onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
            className={errors.endpoint_url ? 'border-destructive' : ''}
          />
          <p className="text-xs text-muted-foreground">
            The full URL of the external API endpoint that accepts POST requests.
          </p>
          {errors.endpoint_url && (
            <p className="text-sm text-destructive">{errors.endpoint_url}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="api_key">API Key {!isEditing && '*'}</Label>
          <Input
            id="api_key"
            type="password"
            placeholder={isEditing ? '••••••••' : 'Enter API key'}
            value={formData.api_key}
            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
            className={errors.api_key ? 'border-destructive' : ''}
          />
          <p className="text-xs text-muted-foreground">
            {isEditing
              ? 'Leave blank to keep the existing key. Your API key is stored encrypted.'
              : 'Your API key will be encrypted and stored securely. Never exposed in UI or logs.'}
          </p>
          {errors.api_key && (
            <p className="text-sm text-destructive">{errors.api_key}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="auth_header_type">Authentication Header Type</Label>
          <Select
            value={formData.auth_header_type}
            onValueChange={(value: WizardFormData['auth_header_type']) =>
              setFormData({ ...formData, auth_header_type: value })
            }
          >
            <SelectTrigger className={errors.auth_header_type ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select auth header type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bearer">Bearer Token (Authorization: Bearer)</SelectItem>
              <SelectItem value="x-api-key">X-API-Key (x-api-key)</SelectItem>
              <SelectItem value="api-key">API-Key (Api-Key)</SelectItem>
              <SelectItem value="custom">Custom Header</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How the API key is sent in request headers.
          </p>
        </div>

        {formData.auth_header_type === 'custom' && (
          <div className="space-y-2 ml-4 pl-4 border-l-2">
            <Label htmlFor="custom_auth_header_name">Custom Header Name *</Label>
            <Input
              id="custom_auth_header_name"
              placeholder="X-Auth-Token"
              value={formData.custom_auth_header_name}
              onChange={(e) => setFormData({ ...formData, custom_auth_header_name: e.target.value })}
              className={errors.custom_auth_header_name ? 'border-destructive' : ''}
            />
            {errors.custom_auth_header_name && (
              <p className="text-sm text-destructive">{errors.custom_auth_header_name}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="custom_headers">Additional Headers (Optional)</Label>
          <Textarea
            id="custom_headers"
            rows={3}
            placeholder={`X-Custom-Header:value\nX-API-Version:2.0`}
            value={formData.custom_headers}
            onChange={(e) => setFormData({ ...formData, custom_headers: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Extra headers to include with every request. One per line in key:value format.
          </p>
        </div>
      </div>

      {/* Session Management */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="requires_session"
            checked={formData.requires_session}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, requires_session: checked as boolean })
            }
            className="mt-0.5"
          />
          <div className="flex-1">
            <Label htmlFor="requires_session" className="cursor-pointer font-medium">
              Enable Session Tracking
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Track multi-turn conversations with the external API. Enabled by default for stateful integrations.
            </p>
          </div>
        </div>

        {formData.requires_session && (
          <div className="ml-7 space-y-4 p-4 border rounded-lg bg-muted/30">
            <div className="space-y-2">
              <Label htmlFor="session_key_name">Session Key Name *</Label>
              <Input
                id="session_key_name"
                placeholder="session_id"
                value={formData.session_key_name}
                onChange={(e) => setFormData({ ...formData, session_key_name: e.target.value })}
                className={errors.session_key_name ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">
                The field name used to track sessions in API requests/responses.
              </p>
              {errors.session_key_name && (
                <p className="text-sm text-destructive">{errors.session_key_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="session_end_strategy">Session End Strategy</Label>
              <Select
                value={formData.session_end_strategy}
                onValueChange={(value: WizardFormData['session_end_strategy']) =>
                  setFormData({ ...formData, session_end_strategy: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="response_field">Response Field</SelectItem>
                  <SelectItem value="keyword">Keyword Detection</SelectItem>
                  <SelectItem value="timeout">Timeout Only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                How to detect when a conversation session has ended.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="session_timeout_seconds">Fallback Timeout (seconds)</Label>
              <Input
                id="session_timeout_seconds"
                type="number"
                min="30"
                max="3600"
                value={formData.session_timeout_seconds}
                onChange={(e) => setFormData({ ...formData, session_timeout_seconds: parseInt(e.target.value) || 300 })}
              />
              <p className="text-xs text-muted-foreground">
                Maximum session duration. Default: 300 seconds (5 minutes).
              </p>
            </div>

            {formData.session_end_strategy === 'response_field' && (
              <div className="space-y-2">
                <Label htmlFor="session_end_response_field">Completion Field Name</Label>
                <Input
                  id="session_end_response_field"
                  placeholder="session_complete"
                  value={formData.session_end_response_field}
                  onChange={(e) => setFormData({ ...formData, session_end_response_field: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Field name in API response indicating session completion.
                </p>
              </div>
            )}

            {formData.session_end_strategy === 'keyword' && (
              <div className="space-y-2">
                <Label htmlFor="session_end_keywords">End Keywords (comma-separated)</Label>
                <Input
                  id="session_end_keywords"
                  placeholder="goodbye, thanks, done"
                  value={formData.session_end_keywords}
                  onChange={(e) => setFormData({ ...formData, session_end_keywords: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Keywords that trigger session end when found in responses.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Response Format - Only for Generic API */}
      {formData.integration_type === 'generic_api' && (
        <div className="space-y-4 pt-4 border-t">
          <div>
            <h3 className="text-lg font-medium">Response Format</h3>
            <p className="text-sm text-muted-foreground">
              Configure how to extract the message from API responses
            </p>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Generic APIs may return responses in custom JSON formats. Specify the JSON path to extract the actual message.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="response_json_path">Response JSON Path *</Label>
            <Input
              id="response_json_path"
              placeholder="data.message or result.0.text"
              value={formData.response_json_path}
              onChange={(e) => setFormData({ ...formData, response_json_path: e.target.value })}
              className={errors.response_json_path ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Dot notation path to the message field. Example: <code className="bg-muted px-1 rounded">data.response.text</code>
            </p>
            {errors.response_json_path && (
              <p className="text-sm text-destructive">{errors.response_json_path}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


function SROConfigSection({
  formData,
  setFormData,
  errors,
}: {
  formData: WizardFormData
  setFormData: (data: WizardFormData) => void
  errors: Record<string, string>
}) {
  const { data: configs, isLoading } = useSROConfigs()

  // Auto-select first config if none selected
  useEffect(() => {
    if (configs && configs.length > 0 && !formData.sro_config_id) {
      setFormData({ ...formData, sro_config_id: configs[0].id })
    }
  }, [configs])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">SRO Configuration</h2>
        <p className="text-sm text-muted-foreground">
          Select which Service Request Orchestrator configuration to use
        </p>
      </div>

      <Alert>
        <ClipboardList className="h-4 w-4" />
        <AlertDescription className="text-xs">
          The SRO module handles service requests internally — no API endpoint or credentials needed.
          Session management is handled automatically via Redis.
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label>SRO Configuration *</Label>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading configurations...</p>
        ) : configs && configs.length > 0 ? (
          <Select
            value={formData.sro_config_id?.toString() || ''}
            onValueChange={(val) => setFormData({ ...formData, sro_config_id: parseInt(val) })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an SRO configuration" />
            </SelectTrigger>
            <SelectContent>
              {configs.map((config: { id: number; name: string; is_enabled: boolean }) => (
                <SelectItem key={config.id} value={config.id.toString()}>
                  {config.name} {!config.is_enabled && '(disabled)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              No SRO configurations found. Create one in the Service Requests section first.
            </AlertDescription>
          </Alert>
        )}
        {errors.sro_config_id && (
          <p className="text-sm text-destructive">{errors.sro_config_id}</p>
        )}
      </div>

      <div className="p-4 bg-muted/30 rounded-lg border space-y-2">
        <h4 className="font-medium text-sm">What SRO handles:</h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>Report new service issues (potholes, fallen trees, etc.)</li>
          <li>Check status of existing service requests</li>
          <li>Multi-turn conversations with entity extraction</li>
          <li>Automatic department routing and priority assignment</li>
        </ul>
      </div>
    </div>
  )
}
